import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Plus,
  Film as FilmIcon,
  GripVertical,
  ChevronDown,
  ChevronUp,
  MoreHorizontal,
  CalendarDays,
  Play,
  CheckCircle2,
  Clock,
  Trash2,
  Edit2,
  Star,
  FileDown,
  Wand2,
  Users,
  MapPin,
  Table2,
  LayoutList,
  ClipboardList,
  Clapperboard,
  FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import { getAuthHeaders } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { API_BASE } from '../config/api';
import { cn } from '../lib/utils';
import { usePdfExport } from '../hooks/usePdfExport';
import Strip, { StripLegend } from './stripboard/Strip';
import AutoBuildDialog from './stripboard/AutoBuildDialog';
import DoodGrid from './stripboard/DoodGrid';

const API = API_BASE;

const padCount = (n) => String(n).padStart(2, '0');

const STATUS_BADGE = {
  planning: { label: 'Planning', icon: Clock, cls: 'bg-muted text-muted-foreground' },
  active: { label: 'Now Shooting', icon: Play, cls: 'bg-primary/15 text-primary' },
  completed: { label: 'Wrapped', icon: CheckCircle2, cls: 'bg-success/15 text-success' },
};

/* ──────────────── Schedule Section ──────────────── */

const ScheduleSection = ({
  schedule,
  scenes,
  isDropTarget,
  draggingId,
  canManage,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragStart,
  onDragEnd,
  onSceneClick,
  onEdit,
  onDelete,
  onActivate,
  onComplete,
  onExport,
  exporting,
  summary,
  dropIndex,
  onStripDragOver,
  onStripDragLeave,
  onStripDrop,
  callSheet,
  onCallSheet,
  onCoverage,
  onDpr,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const badge = STATUS_BADGE[schedule.status] || STATUS_BADGE.planning;
  const BadgeIcon = badge.icon;

  // Progress
  const total = scenes.length;
  const done = scenes.filter(s => s.status === 'done' || s.status === 'shot').length;
  const progressPct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <section
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={cn(
        'rounded-2xl border border-border bg-card/50 overflow-hidden transition-all',
        isDropTarget && 'ring-2 ring-primary/30 bg-primary/5',
        schedule.status === 'active' && 'border-primary/40'
      )}
    >
      {/* Header */}
      <div className={cn(
        'flex items-center gap-3 px-4 py-3 border-b border-border',
        schedule.status === 'active' && 'bg-primary/5'
      )}>
        <button type="button" onClick={() => setCollapsed(!collapsed)} className="shrink-0">
          {collapsed ? <ChevronDown className="size-4 text-muted-foreground" /> : <ChevronUp className="size-4 text-muted-foreground" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <h3 className="text-sm font-bold tracking-tight truncate">{schedule.name}</h3>
            <span className={cn('inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full', badge.cls)}>
              <BadgeIcon className="size-3" /> {badge.label}
            </span>
          </div>
          <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
            {schedule.start_date && (
              <span className="inline-flex items-center gap-1">
                <CalendarDays className="size-3" />
                {schedule.start_date}{schedule.end_date ? ` — ${schedule.end_date}` : ''}
              </span>
            )}
            <span className="tabular-nums">{total} {total === 1 ? 'scene' : 'scenes'}</span>
            {total > 0 && <span className="tabular-nums">{progressPct}% shot</span>}
            {schedule.auto_built && <span className="inline-flex items-center gap-1 text-primary/80"><Wand2 className="size-3" /> auto</span>}
            {callSheet && (
              <button
                type="button"
                onClick={() => onCallSheet?.(schedule)}
                className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold border',
                  callSheet.status === 'sent' ? 'border-green-500/40 bg-green-500/10 text-green-700 dark:text-green-300' : 'border-border text-muted-foreground')}
                title={callSheet.status === 'sent' ? `${callSheet.confirmed} of ${callSheet.total} confirmed` : 'Call sheet drafted, not sent'}
                data-testid={`call-sheet-chip-${schedule.id}`}
              >
                <ClipboardList className="size-3" />
                {callSheet.status === 'sent' ? `Call sheet · ${callSheet.confirmed}/${callSheet.total} confirmed` : 'Call sheet draft'}
              </button>
            )}
          </div>
          {/* Day header intel (Task B3): where, who, how much — the three
              questions an AD answers before anything else. */}
          {summary && total > 0 && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground mt-1" data-testid={`day-summary-${schedule.id}`}>
              {summary.locations?.length > 0 && (
                <span className="inline-flex items-center gap-1 min-w-0" title={summary.locations.join(' · ')}>
                  <MapPin className="size-3 shrink-0" /><span className="truncate max-w-[16rem]">{summary.locations.join(' · ')}</span>
                </span>
              )}
              <span className="inline-flex items-center gap-1" title={summary.cast_names?.join(', ') || 'No cast tagged'}>
                <Users className="size-3" />{summary.cast_count} cast
              </span>
              <span className="inline-flex items-center gap-1 tabular-nums" title="Shots covered / planned">
                <FilmIcon className="size-3" />{summary.shots_covered}/{summary.shots_total} shots
              </span>
            </div>
          )}
        </div>

        {/* Progress bar */}
        {total > 0 && (
          <div className="hidden sm:block w-20">
            <div className="h-1.5 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full rounded-full bg-primary transition-all"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        )}

        {/* Export is for everyone who can see the day — viewers included. The
            PDF is what gets handed to the crew. */}
        {onExport && (
          <button
            type="button"
            title="Export day breakdown (PDF)"
            aria-label={`Export ${schedule.name} as PDF`}
            data-testid={`schedule-export-pdf-${schedule.id}`}
            disabled={exporting}
            onClick={() => onExport(schedule)}
            className="size-7 rounded-md flex items-center justify-center text-muted-foreground hover:bg-accent hover:text-foreground transition-colors disabled:opacity-50"
          >
            <FileDown className="size-4" />
          </button>
        )}

        {onCoverage && (
          <button
            type="button"
            title="On-set coverage (phone view)"
            aria-label={`On-set coverage for ${schedule.name}`}
            data-testid={`schedule-coverage-${schedule.id}`}
            onClick={() => onCoverage(schedule)}
            className="size-7 rounded-md flex items-center justify-center text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          >
            <Clapperboard className="size-4" />
          </button>
        )}

        {onCallSheet && (
          <button
            type="button"
            title={callSheet ? 'Open call sheet' : 'Create call sheet'}
            aria-label={`Call sheet for ${schedule.name}`}
            data-testid={`schedule-call-sheet-${schedule.id}`}
            onClick={() => onCallSheet(schedule)}
            className="size-7 rounded-md flex items-center justify-center text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          >
            <ClipboardList className="size-4" />
          </button>
        )}

        {canManage && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="size-7 rounded-md flex items-center justify-center text-muted-foreground hover:bg-accent hover:text-foreground transition-colors">
                <MoreHorizontal className="size-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onSelect={() => onEdit(schedule)}>
                <Edit2 className="size-4 mr-2" /> Edit day
              </DropdownMenuItem>
              {onCallSheet && (
                <DropdownMenuItem onSelect={() => onCallSheet(schedule)}>
                  <ClipboardList className="size-4 mr-2" /> {callSheet ? 'Open call sheet' : 'Create call sheet'}
                </DropdownMenuItem>
              )}
              {onCoverage && (
                <DropdownMenuItem onSelect={() => onCoverage(schedule)}>
                  <Clapperboard className="size-4 mr-2" /> On-set coverage
                </DropdownMenuItem>
              )}
              {onDpr && (
                <DropdownMenuItem onSelect={() => onDpr(schedule)}>
                  <FileText className="size-4 mr-2" /> Daily production report
                </DropdownMenuItem>
              )}
              {schedule.status !== 'active' && (
                <DropdownMenuItem onSelect={() => onActivate(schedule.id)}>
                  <Play className="size-4 mr-2" /> Start Shooting
                </DropdownMenuItem>
              )}
              {schedule.status === 'active' && (
                <DropdownMenuItem onSelect={() => onComplete(schedule.id)}>
                  <CheckCircle2 className="size-4 mr-2" /> Mark Wrapped
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => onDelete(schedule.id)} className="text-destructive focus:text-destructive">
                <Trash2 className="size-4 mr-2" /> Delete Schedule
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Body — strips in shooting order */}
      {!collapsed && (
        <div className="p-2 space-y-1 min-h-[60px]">
          {scenes.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-card/30 py-6 text-center">
              <p className="text-xs text-muted-foreground">Drag scenes here to schedule them</p>
            </div>
          ) : (
            scenes.map((scene, i) => (
              <Strip
                key={scene.id}
                scene={scene}
                draggable={canManage}
                onDragStart={(e) => onDragStart(e, scene)}
                onDragEnd={onDragEnd}
                onDragOver={onStripDragOver(schedule.id, i)}
                onDragLeave={onStripDragLeave}
                onDrop={onStripDrop(schedule.id, i)}
                isDragging={draggingId === scene.id}
                dropIndicator={dropIndex && dropIndex.scheduleId === schedule.id && dropIndex.index === i ? dropIndex.side : null}
                onClick={onSceneClick}
              />
            ))
          )}
        </div>
      )}
    </section>
  );
};

/* ══════════════════════ Main ScheduleBoard ══════════════════════ */

const ScheduleBoard = ({ filmId, canManage, onSceneClick }) => {
  const navigate = useNavigate();
  const { workspaceId } = useParams();
  const [callSheets, setCallSheets] = useState({});
  const [schedules, setSchedules] = useState([]);
  const [scenes, setScenes] = useState([]); // enriched with schedule info
  const [unscheduledCount, setUnscheduledCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const [summaries, setSummaries] = useState({});
  const [view, setView] = useState('board'); // 'board' | 'dood'
  const [doodKey, setDoodKey] = useState(0);
  const [autoOpen, setAutoOpen] = useState(false);

  // DnD
  const [draggingId, setDraggingId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null); // schedule_id or 'unscheduled'
  const [dropIndex, setDropIndex] = useState(null);   // {scheduleId, index, side: 'before'|'after'}

  // Schedule CRUD dialog
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [schName, setSchName] = useState('');
  const [schDesc, setSchDesc] = useState('');
  const [schStart, setSchStart] = useState('');
  const [schEnd, setSchEnd] = useState('');
  const [saving, setSaving] = useState(false);

  // Unscheduled section collapsed?
  const [unschCollapsed, setUnschCollapsed] = useState(false);

  /* ── fetch ── */
  const fetchData = useCallback(async () => {
    try {
      const [schRes, scenesRes, sumRes] = await Promise.all([
        axios.get(`${API}/films/${filmId}/schedules`, { headers: getAuthHeaders() }),
        axios.get(`${API}/films/${filmId}/scenes-with-schedules`, { headers: getAuthHeaders() }),
        axios.get(`${API}/films/${filmId}/schedules/summary`, { headers: getAuthHeaders() }).catch(() => ({ data: {} })),
      ]);
      setSchedules(schRes.data.schedules || []);
      setUnscheduledCount(schRes.data.unscheduled_count || 0);
      setScenes(scenesRes.data || []);
      setSummaries(sumRes.data || {});
      setDoodKey((k) => k + 1);
      axios.get(`${API}/films/${filmId}/call-sheets`, { headers: getAuthHeaders() })
        .then((r) => setCallSheets(r.data || {}))
        .catch(() => setCallSheets({}));
    } catch (err) {
      console.error('Failed to load schedule data:', err);
      toast.error('Failed to load shooting schedules');
    } finally {
      setLoading(false);
    }
  }, [filmId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ── derived data ── */
  const unscheduledScenes = useMemo(
    () => scenes.filter(s => !s.schedule_id).sort((a, b) => {
      const an = parseInt(String(a.scene_number || '0').match(/^\d+/)?.[0], 10) || 0;
      const bn = parseInt(String(b.scene_number || '0').match(/^\d+/)?.[0], 10) || 0;
      return an - bn;
    }),
    [scenes]
  );

  const scenesBySchedule = useMemo(() => {
    const map = {};
    for (const sch of schedules) map[sch.id] = [];
    for (const s of scenes) {
      if (s.schedule_id && map[s.schedule_id]) {
        map[s.schedule_id].push(s);
      }
    }
    // Sort by schedule_sort_order
    for (const key of Object.keys(map)) {
      map[key].sort((a, b) => (a.schedule_sort_order || 0) - (b.schedule_sort_order || 0));
    }
    return map;
  }, [scenes, schedules]);

  /* ── DnD ── */
  const onDragStart = (e, scene) => {
    if (!canManage) { e.preventDefault(); return; }
    setDraggingId(scene.id);
    try {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', scene.id);
    } catch {}
  };
  const onDragEnd = () => { setDraggingId(null); setDropTarget(null); setDropIndex(null); };

  const makeDragOver = (target) => (e) => {
    if (!draggingId) return;
    e.preventDefault();
    if (dropTarget !== target) setDropTarget(target);
    if (dropIndex) setDropIndex(null); // strips stop propagation, so this is the gap between them
  };
  const makeDragLeave = (target) => () => {
    if (dropTarget === target) setDropTarget(null);
  };

  /**
   * Move a strip: into a day (optionally at a position), or back to the
   * backlog. Uses the existing assign + reorder endpoints — the board is a
   * view over them, not a second scheduling system.
   */
  const moveScene = async (sceneId, targetScheduleId, insertIndex = null) => {
    const scene = scenes.find(s => s.id === sceneId);
    if (!scene) return;
    const fromId = scene.schedule_id || null;
    const toId = targetScheduleId || null;
    if (!fromId && !toId) return;

    // New order for the target day, with the moved strip at insertIndex.
    let targetOrder = null;
    if (toId) {
      const others = (scenesBySchedule[toId] || []).filter(s => s.id !== sceneId).map(s => s.id);
      const at = insertIndex == null ? others.length : Math.max(0, Math.min(insertIndex, others.length));
      targetOrder = [...others.slice(0, at), sceneId, ...others.slice(at)];
    }
    if (fromId === toId && targetOrder) {
      const current = (scenesBySchedule[toId] || []).map(s => s.id);
      if (current.join() === targetOrder.join()) return; // dropped where it was
    }

    // Optimistic
    setScenes(prev => prev.map(s => {
      if (s.id === sceneId) return { ...s, schedule_id: toId, schedule_sort_order: targetOrder ? targetOrder.indexOf(sceneId) : 0 };
      if (toId && s.schedule_id === toId && targetOrder) return { ...s, schedule_sort_order: targetOrder.indexOf(s.id) };
      return s;
    }));

    try {
      if (fromId !== toId) {
        await axios.put(
          `${API}/films/${filmId}/scenes/${sceneId}/schedule`,
          { scene_id: sceneId, schedule_id: toId },
          { headers: getAuthHeaders() }
        );
      }
      if (targetOrder) {
        await axios.put(`${API}/films/${filmId}/schedules/${toId}/reorder`, { scene_ids: targetOrder }, { headers: getAuthHeaders() });
      }
      if (fromId !== toId) toast.success(toId ? 'Scene scheduled' : 'Scene unscheduled');
      // Summaries are cheap and the header intel must follow the strips.
      axios.get(`${API}/films/${filmId}/schedules/summary`, { headers: getAuthHeaders() })
        .then((r) => { setSummaries(r.data || {}); setDoodKey((k) => k + 1); })
        .catch(() => {});
    } catch (err) {
      console.error('Failed to move scene:', err);
      toast.error('Could not move scene');
      fetchData();
    }
  };

  const makeDrop = (targetScheduleId) => async (e) => {
    e.preventDefault();
    const sceneId = draggingId || e.dataTransfer?.getData('text/plain');
    onDragEnd();
    if (!sceneId) return;
    await moveScene(sceneId, targetScheduleId, null);
  };

  // Per-strip handlers: hovering the top half of a strip inserts before it,
  // the bottom half after — the standard list-reorder affordance.
  const onStripDragOver = (scheduleId, index) => (e) => {
    if (!draggingId) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const side = e.clientY - rect.top < rect.height / 2 ? 'before' : 'after';
    if (!dropIndex || dropIndex.scheduleId !== scheduleId || dropIndex.index !== index || dropIndex.side !== side) {
      setDropIndex({ scheduleId, index, side });
    }
    if (dropTarget !== scheduleId) setDropTarget(scheduleId);
  };
  const onStripDragLeave = () => {};
  const onStripDrop = (scheduleId, index) => async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const sceneId = draggingId || e.dataTransfer?.getData('text/plain');
    const side = dropIndex?.side || 'after';
    onDragEnd();
    if (!sceneId) return;
    const dragged = scenes.find(s => s.id === sceneId);
    // Index among the *other* strips in this day.
    const siblings = (scenesBySchedule[scheduleId] || []).filter(s => s.id !== sceneId);
    const anchorId = (scenesBySchedule[scheduleId] || [])[index]?.id;
    if (anchorId === sceneId) return;
    let at = siblings.findIndex(s => s.id === anchorId);
    if (at < 0) at = siblings.length;
    if (side === 'after') at += 1;
    await moveScene(sceneId, scheduleId, at);
    void dragged;
  };

  /* ── Call sheets (M3.1) ── */
  const openCallSheet = (sch) => {
    const base = workspaceId ? `/workspaces/${workspaceId}/films/${filmId}` : `/films/${filmId}`;
    navigate(`${base}/days/${sch.id}/call-sheet`);
  };

  const openDpr = (sch) => {
    const base = workspaceId ? `/workspaces/${workspaceId}/films/${filmId}` : `/films/${filmId}`;
    navigate(`${base}/days/${sch.id}/dpr`);
  };
  const openCoverage = (sch) => {
    const base = workspaceId ? `/workspaces/${workspaceId}/films/${filmId}` : `/films/${filmId}`;
    navigate(`${base}/days/${sch.id}/coverage`);
  };

  /* ── Auto-build days (M2.1) ── */
  const hasAutoBuilt = schedules.some(s => s.auto_built);
  const onAutoBuilt = async (created) => {
    await fetchData();
    const ids = created.map(c => c.id);
    toast.success(`Built ${created.length} shoot ${created.length === 1 ? 'day' : 'days'}`, {
      description: 'Drag strips between days to adjust. Undo removes the days and returns the scenes to the backlog.',
      duration: 8000,
      action: {
        label: 'Undo',
        onClick: async () => {
          try {
            await Promise.all(ids.map(id => axios.delete(`${API}/films/${filmId}/schedules/${id}`, { headers: getAuthHeaders() })));
            toast.success('Auto-built days removed');
          } catch (err) {
            toast.error('Could not remove all the days');
          }
          fetchData();
        },
      },
    });
  };

  /* ── Schedule CRUD ── */
  const openCreateSchedule = () => {
    setEditingSchedule(null);
    setSchName(''); setSchDesc(''); setSchStart(''); setSchEnd('');
    setScheduleDialogOpen(true);
  };

  const openEditSchedule = (sch) => {
    setEditingSchedule(sch);
    setSchName(sch.name);
    setSchDesc(sch.description || '');
    setSchStart(sch.start_date || '');
    setSchEnd(sch.end_date || '');
    setScheduleDialogOpen(true);
  };

  const saveSchedule = async () => {
    if (!schName.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: schName,
        description: schDesc || null,
        start_date: schStart || null,
        end_date: schEnd || null,
      };
      if (editingSchedule) {
        await axios.put(`${API}/films/${filmId}/schedules/${editingSchedule.id}`, payload, { headers: getAuthHeaders() });
        toast.success('Schedule updated');
      } else {
        await axios.post(`${API}/films/${filmId}/schedules`, payload, { headers: getAuthHeaders() });
        toast.success('Schedule created');
      }
      setScheduleDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save schedule');
    } finally { setSaving(false); }
  };

  const deleteSchedule = async (schId) => {
    if (!window.confirm('Delete this day? Its scenes go back to the backlog.')) return;
    try {
      await axios.delete(`${API}/films/${filmId}/schedules/${schId}`, { headers: getAuthHeaders() });
      toast.success('Schedule deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete schedule');
    }
  };

  const { exportPdf, exporting: exportingPdf } = usePdfExport();
  const exportSchedule = (sch) => exportPdf(`/films/${filmId}/schedules/${sch.id}/export/day.pdf`, {
    kind: 'day',
    fallbackName: `FrameFlow — ${sch.name}.pdf`,
  });

  const activateSchedule = async (schId) => {
    try {
      await axios.put(`${API}/films/${filmId}/schedules/${schId}/activate`, {}, { headers: getAuthHeaders() });
      toast.success('Schedule set to Now Shooting');
      fetchData();
    } catch (err) {
      toast.error('Failed to activate schedule');
    }
  };

  const completeSchedule = async (schId) => {
    try {
      await axios.put(`${API}/films/${filmId}/schedules/${schId}`, { status: 'completed' }, { headers: getAuthHeaders() });
      toast.success('Schedule marked as Wrapped');
      fetchData();
    } catch (err) {
      toast.error('Failed to update schedule');
    }
  };

  /* ── render ── */
  if (loading) {
    return (
      <div className="space-y-4">
        {[0, 1, 2].map(i => (
          <div key={i} className="h-32 rounded-2xl bg-muted/50 animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Action bar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-lg border border-border p-0.5 bg-muted/40" role="tablist" aria-label="Schedule view">
          <button
            type="button" role="tab" aria-selected={view === 'board'} onClick={() => setView('board')}
            className={cn('inline-flex items-center gap-1.5 px-2.5 h-7 rounded-md text-xs font-medium transition-colors',
              view === 'board' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground')}
            data-testid="view-board"
          >
            <LayoutList className="size-3.5" /> Stripboard
          </button>
          <button
            type="button" role="tab" aria-selected={view === 'dood'} onClick={() => setView('dood')}
            className={cn('inline-flex items-center gap-1.5 px-2.5 h-7 rounded-md text-xs font-medium transition-colors',
              view === 'dood' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground')}
            data-testid="view-dood"
          >
            <Table2 className="size-3.5" /> Day out of days
          </button>
        </div>
        <p className="text-xs text-muted-foreground flex-1 min-w-[10rem]">
          {schedules.length} {schedules.length === 1 ? 'day' : 'days'} · {unscheduledScenes.length} unscheduled
        </p>
        {canManage && view === 'board' && (
          <>
            <Button
              size="sm" variant="outline" className="rounded-xl gap-2 h-9"
              onClick={() => setAutoOpen(true)}
              disabled={unscheduledScenes.length === 0}
              title={unscheduledScenes.length === 0 ? 'Every scene is already on a day' : 'Group unscheduled scenes into days by location and light'}
              data-testid="autobuild-open"
            >
              <Wand2 className="size-4" /> Auto-build days
            </Button>
            <Button size="sm" className="rounded-xl gap-2 h-9" onClick={openCreateSchedule}>
              <Plus className="size-4" /> New day
            </Button>
          </>
        )}
      </div>

      {view === 'dood' && <DoodGrid filmId={filmId} refreshKey={doodKey} />}

      {view === 'board' && (<>
      <StripLegend />

      {/* Unscheduled (Backlog) */}
      <section
        onDragOver={makeDragOver('unscheduled')}
        onDragLeave={makeDragLeave('unscheduled')}
        onDrop={makeDrop(null)}
        className={cn(
          'rounded-2xl border border-dashed border-border bg-card/30 overflow-hidden transition-all',
          dropTarget === 'unscheduled' && 'ring-2 ring-primary/30 bg-primary/5 border-solid'
        )}
      >
        <button
          type="button"
          onClick={() => setUnschCollapsed(!unschCollapsed)}
          className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-accent/40 transition-colors"
        >
          {unschCollapsed ? <ChevronDown className="size-4 text-muted-foreground" /> : <ChevronUp className="size-4 text-muted-foreground" />}
          <h3 className="text-sm font-bold tracking-tight">Unscheduled</h3>
          <span className="inline-flex items-center px-2 min-w-[28px] h-[20px] justify-center rounded-full text-[11px] font-bold tabular-nums bg-muted text-muted-foreground">
            {unscheduledScenes.length}
          </span>
          <span className="text-[11px] text-muted-foreground ml-auto hidden sm:inline">Backlog — drag strips onto a day, or auto-build</span>
        </button>
        {!unschCollapsed && (
          <div className="px-3 pb-3 space-y-2">
            {unscheduledScenes.length === 0 ? (
              <div className="py-4 text-center">
                <p className="text-xs text-muted-foreground">All scenes are scheduled!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-1">
                {unscheduledScenes.map(scene => (
                  <Strip
                    key={scene.id}
                    scene={scene}
                    compact
                    draggable={canManage}
                    onDragStart={(e) => onDragStart(e, scene)}
                    onDragEnd={onDragEnd}
                    isDragging={draggingId === scene.id}
                    onClick={onSceneClick}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      {/* Schedule Sections */}
      {schedules.length === 0 && (
        <div className="rounded-2xl border border-dashed border-border bg-card/40 p-10 text-center">
          <CalendarDays className="size-8 text-muted-foreground/40 mx-auto mb-3" />
          <h3 className="text-lg font-bold tracking-tight mb-1">No shoot days yet</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto mb-4">
            One day = one strip of scenes. Let FrameFlow group your scenes by location and light, then drag to adjust — or start a day by hand.
          </p>
          {canManage && (
            <div className="flex flex-wrap justify-center gap-2">
              <Button onClick={() => setAutoOpen(true)} className="rounded-xl gap-2" disabled={unscheduledScenes.length === 0} data-testid="autobuild-open-empty">
                <Wand2 className="size-4" /> Auto-build days
              </Button>
              <Button variant="outline" onClick={openCreateSchedule} className="rounded-xl gap-2">
                <Plus className="size-4" /> New day
              </Button>
            </div>
          )}
        </div>
      )}

      {schedules.map(sch => (
        <ScheduleSection
          key={sch.id}
          schedule={sch}
          scenes={scenesBySchedule[sch.id] || []}
          isDropTarget={dropTarget === sch.id}
          draggingId={draggingId}
          canManage={canManage}
          onDragOver={makeDragOver(sch.id)}
          onDragLeave={makeDragLeave(sch.id)}
          onDrop={makeDrop(sch.id)}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onSceneClick={onSceneClick}
          onEdit={openEditSchedule}
          onDelete={deleteSchedule}
          onActivate={activateSchedule}
          onComplete={completeSchedule}
          onExport={exportSchedule}
          exporting={exportingPdf}
          summary={summaries[sch.id]}
          dropIndex={dropIndex}
          onStripDragOver={onStripDragOver}
          onStripDragLeave={onStripDragLeave}
          onStripDrop={onStripDrop}
          callSheet={callSheets[sch.id]}
          onCallSheet={(canManage || callSheets[sch.id]) ? openCallSheet : null}
          onCoverage={canManage ? openCoverage : null}
          onDpr={canManage ? openDpr : null}
        />
      ))}
      </>)}

      <AutoBuildDialog
        open={autoOpen}
        onOpenChange={setAutoOpen}
        filmId={filmId}
        unscheduledCount={unscheduledScenes.length}
        hasAutoBuilt={hasAutoBuilt}
        onBuilt={onAutoBuilt}
      />

      {/* Create/Edit Schedule Dialog */}
      <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingSchedule ? 'Edit day' : 'New shoot day'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={schName}
                onChange={(e) => setSchName(e.target.value)}
                placeholder="e.g., Day 3 — Sekhar house"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={schDesc}
                onChange={(e) => setSchDesc(e.target.value)}
                placeholder="Notes for the day — call time, company move, permissions..."
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date</Label>
                <Input type="date" value={schStart} onChange={(e) => setSchStart(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>End date (multi-day block)</Label>
                <Input type="date" value={schEnd} onChange={(e) => setSchEnd(e.target.value)} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveSchedule} disabled={saving}>
              {saving ? 'Saving...' : editingSchedule ? 'Update' : 'Create day'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ScheduleBoard;
